(() => {
  const normalize = (value) => String(value || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLocaleLowerCase().replace(/ß/g, 'ss').replace(/(\d),(\d)/g, '$1.$2').replace(/(\d)(ml|oz|mm|cm|kg|yd|g|l)\b/g, '$1 $2').replace(/[^a-z0-9.]+/g, ' ').trim();
  const identifier = (value) => String(value || '').toLowerCase().replace(/\s/g, '');
  const isXLFragranceProduct = (product) => /\/iid-ghostbond-xl-scented$/.test(product.url);
  const rank = (products, query) => {
    const words = normalize(query).split(/\s+/).filter(Boolean);
    const phrase = normalize(query);
    const code = identifier(query);
    const ranked = [];
    products.forEach((product, order) => {
      const title = normalize(product.title);
      const productText = normalize([product.title, product.vendor, product.category, product.type, ...(product.tags || [])].join(' '));
      const nativeVariant = String(product.matched_url || '').match(/[?&]variant=(\d+)/)?.[1];
      const variants = (product.variants || []).map((variant, index) => {
        const variantTitle = normalize(variant.title);
        const text = `${productText} ${variantTitle} ${normalize(variant.sku)} ${normalize(variant.barcode)}`;
        const exactCode = code && [identifier(variant.sku), identifier(variant.barcode)].includes(code);
        const exactPhrase = title === phrase || variantTitle === phrase || `${title} ${variantTitle}` === phrase;
        const allWords = words.every((word) => /^\d+(?:\.\d+)?$/.test(word) ? text.split(/\s+/).includes(word) : text.includes(word));
        const tier = exactCode ? 0 : exactPhrase ? 1 : allWords ? 2 : 3;
        const score = words.reduce((total, word) => total + (variantTitle.includes(word) ? 12 : 0) + (title.includes(word) ? 4 : 0), 0);
        const volume = Number(variant.volume_ml);
        const defaultCost = variant.id === product.default_variant ? -1 : volume > 0 ? volume : Infinity;
        return { product, variant, tier, score, defaultCost, nativeMatch: variant.id === nativeVariant, order, index };
      });
      variants.sort((a, b) => a.tier - b.tier || b.score - a.score || (a.defaultCost === b.defaultCost ? 0 : a.defaultCost - b.defaultCost) || Number(b.variant.available) - Number(a.variant.available) || Number(b.nativeMatch) - Number(a.nativeMatch) || a.index - b.index);
      if (isXLFragranceProduct(product)) {
        const fragrances = new Set();
        variants.forEach((entry) => {
          const fragrance = normalize(entry.variant.title.split('·')[0]);
          if (fragrances.has(fragrance)) return;
          fragrances.add(fragrance);
          ranked.push(entry);
        });
      } else if (variants.length) ranked.push(variants[0]);
    });
    ranked.sort((a, b) => a.tier - b.tier || Number(b.variant.available) - Number(a.variant.available) || b.score - a.score || a.order - b.order);
    const exactCode = ranked.some((entry) => entry.tier === 0);
    const directMatches = ranked.some((entry) => entry.tier < 3);
    return ranked.filter((entry) => (!exactCode || entry.tier === 0) && (!directMatches || entry.tier < 3)).slice(0, 8);
  };
  const highlight = (element, text, query) => {
    const terms = [...new Set(String(query).trim().split(/\s+/).filter((word) => word.length >= 2))].sort((a, b) => b.length - a.length);
    if (!terms.length) { element.textContent = text; return; }
    const pattern = new RegExp(`(${terms.map((word) => word.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('|')})`, 'gi');
    String(text).split(pattern).forEach((part, index) => {
      if (index % 2) { const mark = document.createElement('mark'); mark.textContent = part; element.append(mark); }
      else element.append(document.createTextNode(part));
    });
  };
  const initialize = (root = document) => {
    root.querySelectorAll('[data-iid-predictive]').forEach((form) => {
      if (form.dataset.iidPredictiveReady) return;
      const input = form.querySelector('input[name="q"]');
      const panel = form.querySelector('[data-iid-predictive-panel]');
      const list = form.querySelector('[data-iid-predictive-list]');
      const message = form.querySelector('[data-iid-predictive-message]');
      const status = form.querySelector('[data-iid-predictive-status]');
      const allLink = form.querySelector('[data-iid-predictive-all]');
      const settings = form.querySelector('[data-iid-predictive-settings]');
      if (!input || !panel || !list || !message || !status || !allLink || !settings) return;
      let labels;
      try { labels = JSON.parse(settings.textContent); } catch { return; }
      form.dataset.iidPredictiveReady = 'true';
      input.setAttribute('autocomplete', 'off');
      const cache = new Map();
      let timer;
      let controller;
      let sequence = 0;
      let active = -1;
      let composing = false;
      let renderedQuery = '';
      let renderedTime = 0;
      let dismissed = false;
      const options = () => [...list.querySelectorAll('[role="option"]')];
      const resetActive = () => {
        options().forEach((option) => option.setAttribute('aria-selected', 'false'));
        active = -1;
        input.removeAttribute('aria-activedescendant');
      };
      const close = () => {
        clearTimeout(timer);
        controller?.abort();
        sequence += 1;
        panel.hidden = true;
        input.setAttribute('aria-expanded', 'false');
        panel.removeAttribute('aria-busy');
        resetActive();
      };
      const open = () => {
        if (dismissed || !form.contains(document.activeElement)) return;
        panel.hidden = false;
        input.setAttribute('aria-expanded', 'true');
      };
      const searchUrl = (query) => {
        const url = new URL(form.action, window.location.origin);
        url.searchParams.set('q', query);
        url.searchParams.set('type', 'product');
        url.searchParams.set('filter.p.tag', 'iid-sites-migration');
        return url.href;
      };
      const render = (products, query) => {
        list.replaceChildren();
        resetActive();
        const results = rank(products, query);
        results.forEach(({ product, variant }, index) => {
          const li = document.createElement('li');
          li.setAttribute('role', 'presentation');
          const option = document.createElement('a');
          option.className = 'iid-predictive-option';
          option.id = `${input.id}-option-${index}`;
          option.setAttribute('role', 'option');
          option.setAttribute('aria-selected', 'false');
          option.tabIndex = -1;
          const url = new URL(product.url, form.action);
          url.searchParams.set('variant', variant.id);
          option.href = url.href;
          const media = document.createElement('span');
          media.className = 'iid-predictive-image';
          if (variant.image) {
            const image = document.createElement('img');
            image.src = variant.image;
            image.alt = '';
            image.width = 64;
            image.height = 72;
            image.decoding = 'async';
            media.append(image);
          }
          const copy = document.createElement('span');
          copy.className = 'iid-predictive-copy';
          const brand = document.createElement('span');
          brand.className = 'iid-predictive-brand';
          brand.textContent = product.vendor;
          const title = document.createElement('span');
          title.className = 'iid-predictive-title';
          const separateVariant = isXLFragranceProduct(product);
          const variantParts = variant.title.split('·').map((part) => part.trim());
          const displayTitle = separateVariant ? `${product.title.replace(/\s+scented\b/i, '')} ${variantParts[0]}` : product.title;
          highlight(title, displayTitle, query);
          copy.append(brand, title);
          if (variant.title && variant.title !== 'Default Title') {
            const subtitle = document.createElement('span');
            subtitle.className = 'iid-predictive-variant';
            highlight(subtitle, separateVariant ? variantParts.slice(1).join(' · ') : variant.title, query);
            copy.append(subtitle);
          }
          const bottom = document.createElement('span');
          bottom.className = 'iid-predictive-details';
          const price = document.createElement('span');
          price.className = 'iid-predictive-price';
          price.textContent = variant.price;
          bottom.append(price);
          if (variant.unit_price) {
            const unitPrice = document.createElement('span');
            unitPrice.className = 'iid-predictive-unit';
            unitPrice.textContent = variant.unit_price;
            bottom.append(unitPrice);
          }
          if (!variant.available) {
            const stock = document.createElement('span');
            stock.className = 'iid-predictive-stock';
            stock.textContent = labels.unavailable;
            bottom.append(stock);
          }
          copy.append(bottom);
          option.append(media, copy);
          li.append(option);
          list.append(li);
        });
        message.hidden = results.length > 0;
        message.textContent = results.length ? '' : labels.empty;
        panel.removeAttribute('aria-busy');
        renderedQuery = query;
        renderedTime = Date.now();
        status.textContent = results.length ? labels.suggestions.replace('[count]', String(results.length)) : labels.empty;
        open();
      };
      const request = async (query, requestSequence) => {
        const cached = cache.get(query.toLowerCase());
        if (cached && Date.now() - cached.time < 20000) { render(cached.products, query); return; }
        controller = new AbortController();
        try {
          const url = new URL(labels.endpoint, window.location.origin);
          url.searchParams.set('q', query);
          url.searchParams.set('resources[type]', 'product');
          url.searchParams.set('resources[limit]', '10');
          url.searchParams.set('resources[options][unavailable_products]', 'last');
          url.searchParams.set('section_id', 'iid-predictive-search');
          const response = await fetch(url.href, { signal: controller.signal, credentials: 'same-origin', headers: { Accept: 'text/html' } });
          if (!response.ok) throw new Error('Predictive search unavailable');
          const html = new DOMParser().parseFromString(await response.text(), 'text/html');
          const payload = html.querySelector('[data-iid-predictive-data]');
          if (!payload) throw new Error('Missing predictive search data');
          const data = JSON.parse(payload.textContent);
          if (!Array.isArray(data.products)) throw new Error('Invalid predictive search data');
          if (requestSequence !== sequence || input.value.trim() !== query) return;
          if (cache.size >= 20) cache.delete(cache.keys().next().value);
          cache.set(query.toLowerCase(), { products: data.products, time: Date.now() });
          render(data.products, query);
        } catch (error) {
          if (error.name === 'AbortError' || requestSequence !== sequence) return;
          panel.removeAttribute('aria-busy');
          message.textContent = labels.error;
          message.hidden = false;
          status.textContent = labels.error;
          open();
        }
      };
      const update = () => {
        close();
        const query = input.value.trim();
        list.replaceChildren();
        renderedQuery = '';
        status.textContent = '';
        if (composing || Array.from(query).length < 2) return;
        dismissed = false;
        allLink.href = searchUrl(query);
        message.textContent = labels.loading;
        message.hidden = false;
        panel.setAttribute('aria-busy', 'true');
        open();
        const requestSequence = sequence;
        timer = setTimeout(() => request(query, requestSequence), 220);
      };
      input.addEventListener('input', update);
      input.addEventListener('compositionstart', () => { composing = true; close(); });
      input.addEventListener('compositionend', () => { composing = false; update(); });
      input.addEventListener('focus', () => { dismissed = false; if (renderedQuery === input.value.trim() && renderedQuery.length >= 2 && Date.now() - renderedTime < 20000) open(); else update(); });
      input.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') { event.preventDefault(); dismissed = true; close(); return; }
        const items = options();
        if (['ArrowDown', 'ArrowUp'].includes(event.key) && items.length) {
          event.preventDefault();
          dismissed = false;
          open();
          const direction = event.key === 'ArrowDown' ? 1 : -1;
          active = active < 0 ? (direction > 0 ? 0 : items.length - 1) : (active + direction + items.length) % items.length;
          items.forEach((item, index) => item.setAttribute('aria-selected', String(index === active)));
          input.setAttribute('aria-activedescendant', items[active].id);
          items[active].scrollIntoView({ block: 'nearest' });
        }
        if (event.key === 'Enter' && !panel.hidden && active >= 0 && items[active]) {
          event.preventDefault();
          items[active].click();
        }
      });
      form.addEventListener('submit', () => close());
      form.addEventListener('focusout', (event) => { if (!form.contains(event.relatedTarget)) close(); });
      document.addEventListener('pointerdown', (event) => { if (!form.contains(event.target)) { dismissed = true; close(); } });
      form.addEventListener('keydown', (event) => { if (event.key === 'Escape') { input.focus(); dismissed = true; close(); } });
    });
  };
  initialize();
  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
})();

